import CompaniesService, {
  ECompanyType,
  type ICompanyFilter,
  type TCompany,
} from "@/api/models/company/company";
import CompanyMeService from "@/api/models/company/companyMe";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { TSelectFieldDynamicProps } from "@/types/selectField";
import type {
  TComposableParams,
  TItemQueryComposableParams,
} from "@/composables/shared/filters/useGeneralFilter";
import {
  splitComposableParams,
  resolveSelectEnabled,
} from "@/composables/shared/filters/useGeneralFilter";
import { createPathParamCrudMutations } from "@/composables/shared/mutations/useCrudMutations";
import { ESubjects } from "@/api/models/auth/ability";
import {
  createQueryKeys,
  useGenericListQuery,
  useGenericPaginatedQuery,
  useGenericQuery,
  type TItemQueryBehaviorOptions,
  type TOwnedFilterParams,
} from "@/composables/shared/queries";

export const CompanyQueryKeys = createQueryKeys(CompaniesService.queryKey, (key) => ({
  list: key<ICompanyFilter>("list"),
  detail: key<string>("detail"),
  paginated: key<ICompanyFilter>("paginated"),
}));

export const getCompanyTypeColor = (type: ECompanyType) =>
  type === ECompanyType.GP_PRACTICES ? "blue-darken-2" : "deep-purple";

export const getCompanyStatusColor = (status: string) => {
  switch (status) {
    case "active":
      return "green-lighten-4";
    case "onboarding":
      return "teal-darken-3";
    case "setup_issues":
      return "amber-darken-1";
    case "dormant":
      return "grey-lighten-2";
    default:
      return "grey";
  }
};

export const useCompanies = (
  filters?: MaybeRefOrGetter<Partial<ICompanyFilter>>,
  enabled: MaybeRefOrGetter<boolean> = true,
  noRetryStatusCodes?: number[]
) => {
  return useGenericListQuery<TCompany, Partial<ICompanyFilter>>({
    queryKey: CompanyQueryKeys.list(),
    params: { filters, enabled },
    fetcher: async (f) => {
      return await CompaniesService.getAll({}, f);
    },
    permission: $permSubjects.PRACTICE__COMPANY,
    noRetryStatusCodes,
  });
};

/**
 * `type` is a required filter on the `practice/company/` list endpoint, so a single MSO's
 * practices (which span both GP and ortho companies) need one request per type, merged together.
 */
export const useMsoCompanies = (
  msoId: MaybeRefOrGetter<string | undefined>,
  enabled: MaybeRefOrGetter<boolean> = true
) => {
  const gpQuery = useCompanies(
    () => ({ mso: toValue(msoId), type: ECompanyType.GP_PRACTICES }),
    enabled,
    [400, 404]
  );
  const orthoQuery = useCompanies(
    () => ({ mso: toValue(msoId), type: ECompanyType.ORTHO_PRACTICES }),
    enabled,
    [400, 404]
  );

  const companies = computed(() => [
    ...(gpQuery.data.value ?? []),
    ...(orthoQuery.data.value ?? []),
  ]);

  return {
    companies,
    isLoading: computed(() => gpQuery.isLoading.value || orthoQuery.isLoading.value),
  };
};

export const useCompaniesPaginated = (params: TOwnedFilterParams<ICompanyFilter> = {}) => {
  return useGenericPaginatedQuery<TCompany, Partial<ICompanyFilter>>({
    queryKey: CompanyQueryKeys.paginated(),
    params,
    fetcher: async (fetchParams) => {
      return await CompaniesService.getPaginatedFiltered(
        {},
        fetchParams.filters,
        fetchParams.pageParam
      );
    },
    permission: $permSubjects.PRACTICE__COMPANY,
  });
};

const DEFAULT_COMPANY_PARAMS: TItemQueryComposableParams<string> = { enabled: true };

export const useCompany = (
  params: MaybeRefOrGetter<TItemQueryComposableParams<string>> = DEFAULT_COMPANY_PARAMS,
  options: TItemQueryBehaviorOptions = {}
) => {
  return useGenericQuery<TCompany, string>({
    queryKey: CompanyQueryKeys.detail(),
    params,
    fetcher: async (id) => {
      return await CompaniesService.get({ id });
    },
    permission: $permSubjects.PRACTICE__COMPANY,
    ...options,
    options: {
      refetchOnMount: true,
    },
  });
};

export const {
  useCreate: useCreateCompany,
  useUpdate: useUpdateCompany,
  useDelete: useDeleteCompany,
} = createPathParamCrudMutations<TCompany, { id?: string }>(CompaniesService, [], {
  entityName: "company",
  permissionSubject: ESubjects.PRACTICE__COMPANY,
  invalidates: [
    CompanyQueryKeys.list(),
    CompanyQueryKeys.paginated(),
    CompanyQueryKeys.detail(),
    CompanyMeService.queryKey,
  ],
  removes: {
    delete: ({ id }) => [CompanyQueryKeys.detail(id)],
  },
});

export const useCompaniesForSelect = (
  extraParams?: TComposableParams & Partial<ICompanyFilter>
): TSelectFieldDynamicProps<TCompany, ICompanyFilter> => {
  const { filters } = splitComposableParams<ICompanyFilter>(extraParams);
  const query = useCompaniesPaginated({
    initial: filters,
    enabled: () => resolveSelectEnabled(extraParams),
  });

  return {
    paginatedQuery: query,
    queryFilters: query.filters,
    filterField: "query",
    queryEnabled: query.queryEnabled,
  };
};
